import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { AsDiv, TitleXS } from '@/atoms/Text'
import { useExperimentContext } from '@/experimentation'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { Modal } from '@/molecules/Modal'
import { useGuildMember } from '@/services/GuildUserService'
import { FeaturedProjectContentCollection, Maybe, ProjectTheme } from '@/types/codegen-contentful'
import { buildAnalyticsLink } from '@/utils/AnalyticsLinkBuilder'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

interface FeaturedProjectTicketsButtonGroupProps {
  campaign?: Maybe<string>
  contentCollection?: Maybe<FeaturedProjectContentCollection>
  href?: Maybe<string>
  guildMemberHref?: Maybe<string>
  sneakPeekUrl?: Maybe<string>
  projectTheme?: Maybe<ProjectTheme>
  projectSlug?: Maybe<string>
  showDavidVideo?: boolean
}

export const FeaturedProjectTicketsButtonGroup: React.FC<FeaturedProjectTicketsButtonGroupProps> = ({
  campaign,
  contentCollection,
  href,
  sneakPeekUrl,
  projectTheme,
  projectSlug,
  showDavidVideo = false,
}) => {
  const { t } = useTranslate('common')
  const track = useSafeTrack()
  const { asPath } = useSafeRouter()
  const { isGuildMember } = useGuildMember()
  const [showModal, setShowModal] = useState(false)
  const { getFeatureValue } = useExperimentContext()
  const duration = projectTheme?.sneakPeekDuration ?? null
  const analyticsLink = buildAnalyticsLink({
    href: href as string,
    path: asPath,
    campaign: campaign as string,
    linkContext: 'featured-section',
    source: 'featured-section',
  })
  const analyticsSneakPeekLink = buildAnalyticsLink({
    href: sneakPeekUrl as string,
    path: asPath,
    campaign: campaign as string,
    linkContext: 'featured-section',
    source: 'featured-section',
  })

  const video = useMemo(() => {
    const content = contentCollection?.items?.[0]
    return {
      title: content?.title ?? 'Trailer',
      url: content?.source ?? '',
    }
  }, [contentCollection?.items])

  const handleLinkClick = () => {
    track('Featured Project Link Clicked', { href })
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault()
    setShowModal(true)
  }
  const { areGuildTicketsAvailable } = useGuildTickets(projectSlug as string)
  const showVideo = Boolean(video?.url || sneakPeekUrl)
  const showSneakPeek = !isGuildMember && sneakPeekUrl
  const canClaimTickets = useMemo(() => {
    return Boolean(areGuildTicketsAvailable)
  }, [areGuildTicketsAvailable])
  const primaryIconColor = showDavidVideo ? 'white' : 'core-gray-950'
  const showClaimTickets = canClaimTickets && getFeatureValue('tickets_claim_tickets_copy', 'claim') === 'claim'
  const guildTicketsText = showClaimTickets
    ? t('claimTickets', 'Claim Tickets')
    : t('useGuildTickets', 'Use Guild Tickets')

  return (
    <div className="flex w-full flex-col justify-center gap-4 md:flex-row">
      <LinkButton
        href={analyticsLink}
        className={classNames('h-12 w-full min-w-[220px] justify-center space-x-2 md:w-fit relative', {
          'text-white !bg-gradient-to-tl !from-[#CE7B3E] !to-[#FBA349] !border-transparent !shadow-dark-1 hover:!from-[#CE7B3E] hover:!to-[#FBA349]':
            showDavidVideo,
        })}
        onClick={handleLinkClick}
        variant="white"
      >
        {showDavidVideo && <div className="absolute inset-0 rounded-[inherit] bg-black/10" aria-hidden="true" />}
        <span className="relative z-10 flex items-center space-x-2">
          {canClaimTickets ? (
            <GuildIconMono color={primaryIconColor} size={22} />
          ) : (
            <TicketIcon color={primaryIconColor} />
          )}
          <TitleXS as={AsDiv}>{canClaimTickets ? guildTicketsText : t('getShowtimes', 'Get Showtimes')}</TitleXS>
        </span>
      </LinkButton>
      <When condition={showVideo}>
        <When condition={showSneakPeek}>
          <LinkButton
            href={analyticsSneakPeekLink}
            className={classNames(
              'h-12 w-full min-w-[220px] justify-center space-x-2 !border-transparent bg-white/20 backdrop-blur-xl hover:bg-white/35 hover:text-white md:w-fit',
              {
                '!shadow-dark-1 !backdrop-blur-3xl !bg-white/15 hover:!bg-white/20': showDavidVideo,
              },
            )}
            variant="white"
            onClick={handleLinkClick}
            outline
          >
            <TitleXS>
              {duration
                ? t('watchFirstMinutes', `Watch The First {{ minutes }} Minutes`, {
                    minutes: duration,
                  })
                : t('watchSneakPeek', 'Watch Sneak Peek')}
            </TitleXS>
          </LinkButton>
        </When>
        <When condition={video?.url && !showSneakPeek}>
          <Button
            className={classNames(
              'h-12 w-full min-w-[220px] justify-center space-x-2 !border-transparent bg-white/20 backdrop-blur-xl hover:bg-white/35 hover:text-white md:w-fit',
              {
                '!shadow-dark-1 !backdrop-blur-3xl !bg-white/15 hover:!bg-white/20': showDavidVideo,
              },
            )}
            variant="white"
            onClick={handleClick}
            outline
          >
            <TitleXS>{t('watchTrailer', 'Watch Trailer')}</TitleXS>
          </Button>
        </When>
      </When>
      <Modal
        className="z-[99999]"
        panelClassesOverride="inline-block w-3/4 xl:w-full max-w-7xl align-middle shadow-xl rounded-2xl transition-all transform overflow-hidden"
        childWrapperClassName="mt-0"
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      >
        <EmbeddedVideo className="w-full rounded-2xl" {...video} />
      </Modal>
    </div>
  )
}
